export interface Oklch {
	l: number;
	c: number;
	h: number;
}

interface LinearRgb {
	r: number;
	g: number;
	b: number;
}

function srgbChannelToLinear(channel: number): number {
	return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}

function hexToLinearRgb(hex: string): LinearRgb {
	return {
		r: srgbChannelToLinear(parseInt(hex.slice(1, 3), 16) / 255),
		g: srgbChannelToLinear(parseInt(hex.slice(3, 5), 16) / 255),
		b: srgbChannelToLinear(parseInt(hex.slice(5, 7), 16) / 255),
	};
}

export function hexToOklch(hex: string): Oklch {
	const { r, g, b } = hexToLinearRgb(hex);

	const lRoot = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
	const mRoot = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
	const sRoot = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);

	const l = 0.2104542553 * lRoot + 0.793617785 * mRoot - 0.0040720468 * sRoot;
	const a = 1.9779984951 * lRoot - 2.428592205 * mRoot + 0.4505937099 * sRoot;
	const bb = 0.0259040371 * lRoot + 0.7827717662 * mRoot - 0.808675766 * sRoot;

	const c = Math.sqrt(a * a + bb * bb);
	let h = Math.atan2(bb, a) * (180 / Math.PI);
	if (h < 0) h += 360;

	return {
		l: parseFloat((l * 100).toFixed(2)),
		c: parseFloat(c.toFixed(4)),
		h: parseFloat(h.toFixed(2)),
	};
}

export function oklchString({ l, c, h }: Oklch): string {
	return `oklch(${l}% ${c} ${h})`;
}

// Relative luminance per WCAG 2.1, computed from the sRGB hex (linear-light channels).
export function relativeLuminance(hex: string): number {
	const { r, g, b } = hexToLinearRgb(hex);
	return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// WCAG contrast ratio between two sRGB hex colours, 1..21.
export function contrastRatio(a: string, b: string): number {
	const la = relativeLuminance(a);
	const lb = relativeLuminance(b);
	const lighter = Math.max(la, lb);
	const darker = Math.min(la, lb);
	return (lighter + 0.05) / (darker + 0.05);
}
